import React, { useState, useEffect, useRef, useCallback } from "react";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

const AudioPlayer = ({ src, isLoading, title, subtitle, onPrev, onNext, hasPrev, hasNext }) => {
    const audioRef = useRef(null);
    const progressRef = useRef(null);
    const speedMenuRef = useRef(null);
    const speedMobileRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [speedOpen, setSpeedOpen] = useState(false);
    const [activeRowVisible, setActiveRowVisible] = useState(true);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onTimeUpdate = () => { if (!isDragging) setCurrentTime(audio.currentTime); };
        const onLoadedMetadata = () => setDuration(audio.duration);
        const onEnded = () => {
            setIsPlaying(false);
            if (hasNext && onNext) onNext();
        };
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);

        return () => {
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
        };
    }, [src, isDragging, hasNext, onNext]);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio && src) {
            audio.load();
            audio.playbackRate = speed;
            audio.play().catch(() => {});
            setCurrentTime(0);
            setDuration(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    useEffect(() => {
        const row = document.querySelector('.al-row-active');
        if (!row) { setActiveRowVisible(true); return; }

        const observer = new IntersectionObserver(
            ([entry]) => setActiveRowVisible(entry.isIntersecting),
            { threshold: 0.1 }
        );
        observer.observe(row);
        return () => observer.disconnect();
    }, [title, src]);

    useEffect(() => {
        if (!speedOpen) return;
        const handler = (e) => {
            const inDesktop = speedMenuRef.current && speedMenuRef.current.contains(e.target);
            const inMobile = speedMobileRef.current && speedMobileRef.current.contains(e.target);
            if (!inDesktop && !inMobile) setSpeedOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [speedOpen]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) { audio.play().catch(() => {}); }
        else { audio.pause(); }
    };

    const selectSpeed = (s) => {
        setSpeed(s);
        setSpeedOpen(false);
        if (audioRef.current) audioRef.current.playbackRate = s;
    };

    const scrollToActive = () => {
        const row = document.querySelector('.al-row-active');
        if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const seekTo = useCallback((e) => {
        if (!progressRef.current || !duration) return;
        const rect = progressRef.current.getBoundingClientRect();
        const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        audioRef.current.currentTime = pos * duration;
        setCurrentTime(pos * duration);
    }, [duration]);

    const handleMouseDown = (e) => {
        setIsDragging(true);
        seekTo(e);
        const onMouseMove = (ev) => seekTo(ev);
        const onMouseUp = (ev) => {
            setIsDragging(false);
            seekTo(ev);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const formatTime = (t) => {
        if (isNaN(t) || t === 0) return '0:00';
        const m = Math.floor(t / 60);
        const s = Math.floor(t % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const progress = duration ? (currentTime / duration) * 100 : 0;

    return (
        <div className="al-player">
            <audio ref={audioRef} preload="metadata">
                <source src={src} type="audio/mpeg" />
            </audio>

            {/* LEFT: artwork + title + mobile controls */}
            <div className="al-player-info">
                <div className="al-player-artwork">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                    </svg>
                </div>
                <div className="al-player-text">
                    <div className="al-player-title">{isLoading ? "Loading..." : title}</div>
                    <div className="al-player-subtitle">{subtitle}</div>
                </div>
                <div className="al-player-info-right">
                    {!activeRowVisible && (
                        <button className="al-player-scroll-btn al-scroll-mobile" onClick={scrollToActive} title="Scroll to current lecture">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="18,15 12,9 6,15"/>
                            </svg>
                        </button>
                    )}
                    <div className="al-speed-mobile-inline" ref={speedMobileRef}>
                        {speedOpen && (
                            <div className="al-speed-dropup">
                                {SPEEDS.map((s) => (
                                    <button
                                        key={s}
                                        className={`al-speed-dropup-item${s === speed ? ' al-speed-dropup-active' : ''}`}
                                        onClick={() => selectSpeed(s)}
                                    >
                                        <span>{s === 1 ? 'Normal' : s + 'x'}</span>
                                        {s === speed && <span className="al-speed-dropup-check">&#10003;</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                        <button className="al-speed-btn-mobile" onClick={() => setSpeedOpen(!speedOpen)}>
                            <span>Speed</span>
                            <span className="al-speed-btn-val">{speed === 1 ? 'Normal' : speed + 'x'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* CENTER: controls + progress */}
            <div className="al-player-center">
                <div className="al-player-controls-row">
                    <button className="al-player-skip-btn" onClick={onPrev} disabled={!hasPrev || isLoading} title="Previous part">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="3" y="5" width="3" height="14" rx="0.5"/><polygon points="21,5 9,12 21,19"/>
                        </svg>
                    </button>
                    <button className="al-player-play-btn" onClick={togglePlay} disabled={isLoading}>
                        {isLoading ? (
                            <svg className="al-player-loading-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <path d="M12 2a10 10 0 0 1 10 10"/>
                            </svg>
                        ) : isPlaying ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <rect x="5" y="3" width="5" height="18" rx="1"/><rect x="14" y="3" width="5" height="18" rx="1"/>
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="6,3 20,12 6,21"/>
                            </svg>
                        )}
                    </button>
                    <button className="al-player-skip-btn" onClick={onNext} disabled={!hasNext || isLoading} title="Next part">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="3,5 15,12 3,19"/><rect x="18" y="5" width="3" height="14" rx="0.5"/>
                        </svg>
                    </button>
                </div>
                <div className="al-player-progress-row">
                    <span className="al-player-time">{formatTime(currentTime)}</span>
                    <div className="al-player-progress" ref={progressRef} onMouseDown={handleMouseDown}>
                        <div className="al-player-progress-fill" style={{ width: `${progress}%` }} />
                        <div className="al-player-progress-thumb" style={{ left: `${progress}%` }} />
                    </div>
                    <span className="al-player-time">{formatTime(duration)}</span>
                </div>
            </div>

            {/* RIGHT: scroll-to (desktop) + speed */}
            <div className="al-player-right" ref={speedMenuRef}>
                {!activeRowVisible && (
                    <button className="al-player-scroll-btn al-scroll-desktop" onClick={scrollToActive} title="Scroll to current lecture">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="18,15 12,9 6,15"/>
                        </svg>
                    </button>
                )}

                {/* Desktop: dropdown */}
                <div className="al-speed-desktop">
                    <button
                        className={`al-speed-trigger${speedOpen ? ' al-speed-trigger-open' : ''}${speed !== 1 ? ' al-speed-trigger-active' : ''}`}
                        onClick={() => setSpeedOpen(!speedOpen)}
                    >
                        {speed === 1 ? '1x' : speed + 'x'}
                    </button>
                    {speedOpen && (
                        <div className="al-speed-menu">
                            {SPEEDS.map((s) => (
                                <button
                                    key={s}
                                    className={`al-speed-option${s === speed ? ' al-speed-option-active' : ''}`}
                                    onClick={() => selectSpeed(s)}
                                >
                                    {s === 1 ? '1x' : s + 'x'}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AudioPlayer;
